import { useEffect, useRef, useState } from 'react';

/**
 * A still from the walkthrough video, for a card that has no photograph.
 *
 * A listing can go live with a video and no photos (a landlord who filmed the
 * flat and stopped there), and a blank card with a house icon reads as broken
 * in a grid of pictures. The browser can show a frame of the video itself: a
 * media fragment (#t=2) asks for the frame two seconds in, which skips the
 * fade from black most walkthroughs open on.
 *
 * Nothing is fetched until the card is near the screen, and then only the
 * file's index and one frame (preload="metadata", and uploads are encoded with
 * faststart). A 360 film's frame is the whole sphere laid flat; cropped to the
 * card it shows the middle of the room, which is the part worth seeing.
 */
export function VideoPoster({ src }: { src: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === 'undefined') { setNear(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  return (
    <div ref={ref} className="lst-video-poster">
      {near && !failed
        ? <video src={`${src}#t=2`} preload="metadata" muted playsInline disablePictureInPicture
            tabIndex={-1} aria-hidden onError={() => setFailed(true)} />
        : <div className="lst-photo-none" aria-hidden>⌂</div>}
    </div>
  );
}
